import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  QuestionMarkCircleIcon,
  BookOpenIcon,
  RocketLaunchIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const Help = () => {
  const [activeCategory, setActiveCategory] = useState('faq');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch help data
  const { data: faqData, isLoading: faqLoading } = useQuery({
    queryKey: ['help-faq'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/help/faq`);
      if (!response.ok) throw new Error('Failed to fetch FAQ');
      return response.json();
    }
  });

  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ['help-documentation'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/help/documentation`);
      if (!response.ok) throw new Error('Failed to fetch documentation');
      return response.json();
    }
  });

  const { data: quickstartData, isLoading: quickstartLoading } = useQuery({
    queryKey: ['help-quickstart'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/help/quickstart`);
      if (!response.ok) throw new Error('Failed to fetch quickstart');
      return response.json();
    }
  });

  const { data: troubleshootingData, isLoading: troubleshootingLoading } = useQuery({
    queryKey: ['help-troubleshooting'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/help/troubleshooting`);
      if (!response.ok) throw new Error('Failed to fetch troubleshooting');
      return response.json();
    }
  });

  const { data: featuresData, isLoading: featuresLoading } = useQuery({
    queryKey: ['help-features'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/help/features`);
      if (!response.ok) throw new Error('Failed to fetch features');
      return response.json();
    }
  });

  const categories = [
    { id: 'faq', name: 'FAQ', icon: QuestionMarkCircleIcon, data: faqData, loading: faqLoading },
    { id: 'quickstart', name: 'Quick Start', icon: RocketLaunchIcon, data: quickstartData, loading: quickstartLoading },
    { id: 'features', name: 'Features', icon: SparklesIcon, data: featuresData, loading: featuresLoading },
    { id: 'documentation', name: 'API Docs', icon: BookOpenIcon, data: docsData, loading: docsLoading },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: WrenchScrewdriverIcon, data: troubleshootingData, loading: troubleshootingLoading }
  ];

  const toggleQuestion = (questionId) => {
    setExpandedQuestion(expandedQuestion === questionId ? null : questionId);
  };

  const filterQuestions = (categories) => {
    if (!searchQuery.trim()) return categories;

    return categories.map(cat => ({
      ...cat,
      questions: cat.questions.filter(q =>
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(cat => cat.questions.length > 0);
  };

  const renderFAQ = () => {
    if (faqLoading) return <LoadingSpinner />;
    if (!faqData) return <div className="text-gray-500">No FAQ data available</div>;

    const filteredCategories = filterQuestions(faqData.categories);

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {filteredCategories.map((category, catIndex) => (
          <div key={catIndex} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{category.category}</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {category.questions.map((item, qIndex) => {
                const questionId = `${catIndex}-${qIndex}`;
                const isExpanded = expandedQuestion === questionId;

                return (
                  <div key={qIndex} className="hover:bg-gray-50 transition-colors">
                    <button
                      onClick={() => toggleQuestion(questionId)}
                      className="w-full px-6 py-4 flex items-start justify-between text-left focus:outline-none"
                    >
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-5 w-5 text-indigo-600" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{item.question}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-6 pb-4 pl-14">
                        <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No questions found matching "{searchQuery}"
          </div>
        )}

        <div className="text-sm text-gray-500 text-center mt-8">
          Last updated: {faqData.last_updated}
        </div>
      </div>
    );
  };

  const renderQuickstart = () => {
    if (quickstartLoading) return <LoadingSpinner />;
    if (!quickstartData) return <div className="text-gray-500">No quickstart data available</div>;

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-8 text-white">
          <h2 className="text-3xl font-bold mb-2">{quickstartData.title}</h2>
          <p className="text-indigo-100">Get up and running in minutes with this step-by-step guide</p>
        </div>

        <div className="space-y-4">
          {quickstartData.steps.map((step, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                      {step.step}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        ⏱️ {step.estimated_time}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start">
                          <span className="text-indigo-600 mr-2">•</span>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Next Steps</h3>
          <ul className="space-y-2">
            {quickstartData.next_steps.map((step, index) => (
              <li key={index} className="flex items-start">
                <span className="text-indigo-600 mr-2">→</span>
                <span className="text-gray-700">{step}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderFeatures = () => {
    if (featuresLoading) return <LoadingSpinner />;
    if (!featuresData) return <div className="text-gray-500">No features data available</div>;

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Core Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {featuresData.core_features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.name}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Benefits:</p>
                  {feature.benefits.map((benefit, bIndex) => (
                    <div key={bIndex} className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span className="text-sm text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI Capabilities</h3>
            <ul className="space-y-2">
              {featuresData.ai_capabilities.map((capability, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span className="text-sm text-gray-700">{capability}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Methodologies</h3>
            <ul className="space-y-2">
              {featuresData.methodology_support.map((methodology, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-purple-600 mr-2">•</span>
                  <span className="text-sm text-gray-700">{methodology}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📁 File Formats</h3>
            <ul className="space-y-2">
              {featuresData.file_formats.map((format, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span className="text-sm text-gray-700">{format}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentation = () => {
    if (docsLoading) return <LoadingSpinner />;
    if (!docsData) return <div className="text-gray-500">No documentation available</div>;

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">API Documentation</h2>
          <p className="text-blue-100">Version {docsData.api_version}</p>
          <p className="text-blue-100 mt-2">Base URL: <code className="bg-blue-700 px-2 py-1 rounded">{docsData.base_url}</code></p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">🔐 Authentication</h3>
          <p className="text-gray-600 mb-3"><strong>Type:</strong> {docsData.authentication.type}</p>
          <p className="text-gray-600 mb-4">{docsData.authentication.description}</p>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Endpoints:</p>
            <ul className="space-y-2">
              {Object.entries(docsData.authentication.endpoints).map(([key, value], index) => (
                <li key={index} className="text-sm">
                  <span className="font-mono bg-indigo-100 text-indigo-800 px-2 py-1 rounded">{value}</span>
                  <span className="text-gray-600 ml-2">- {key}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-900">API Endpoints</h3>
          {docsData.endpoint_categories.map((category, catIndex) => (
            <div key={catIndex} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900">{category.category}</h4>
                <p className="text-sm text-gray-600 mt-1">{category.description}</p>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {category.endpoints.map((endpoint, epIndex) => (
                    <div key={epIndex} className="flex items-start space-x-3 pb-3 border-b border-gray-100 last:border-0">
                      <span className={`text-xs font-bold px-2 py-1 rounded ${endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                          endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                            endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                        }`}>
                        {endpoint.method}
                      </span>
                      <div className="flex-1">
                        <code className="text-sm font-mono text-gray-800">{endpoint.path}</code>
                        <p className="text-sm text-gray-600 mt-1">{endpoint.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">⚠️ Error Codes</h3>
          <div className="space-y-3">
            {docsData.common_errors.map((error, index) => (
              <div key={index} className="flex items-start space-x-3">
                <span className="font-mono text-red-600 font-bold">{error.code}</span>
                <span className="text-gray-600">{error.meaning}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">🔗 Additional Resources</h3>
          <div className="space-y-2">
            <a href={docsData.interactive_docs} className="text-indigo-600 hover:text-indigo-800 block">
              → Interactive API Documentation
            </a>
            <a href={docsData.openapi_spec} className="text-indigo-600 hover:text-indigo-800 block">
              → OpenAPI Specification
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderTroubleshooting = () => {
    if (troubleshootingLoading) return <LoadingSpinner />;
    if (!troubleshootingData) return <div className="text-gray-500">No troubleshooting data available</div>;

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg shadow-lg p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">Troubleshooting Guide</h2>
          <p className="text-yellow-100">Common issues and their solutions</p>
        </div>

        <div className="space-y-4">
          {troubleshootingData.common_issues.map((issue, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-red-50 px-6 py-4 border-b border-red-200">
                <h3 className="text-lg font-semibold text-gray-900">🔴 {issue.issue}</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Symptoms:</h4>
                  <ul className="space-y-1">
                    {issue.symptoms.map((symptom, sIndex) => (
                      <li key={sIndex} className="text-gray-600 ml-4">• {symptom}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Causes:</h4>
                  <ul className="space-y-1">
                    {issue.causes.map((cause, cIndex) => (
                      <li key={cIndex} className="text-gray-600 ml-4">• {cause}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Solutions:</h4>
                  <ol className="space-y-2">
                    {issue.solutions.map((solution, solIndex) => (
                      <li key={solIndex} className="text-gray-700 ml-4">
                        <span className="font-medium text-indigo-600">{solIndex + 1}.</span> {solution}
                      </li>
                    ))}
                  </ol>
                </div>
                {issue.workaround && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">💡 Workaround:</h4>
                    <p className="text-blue-800">{issue.workaround}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">📋 Error Code Reference</h3>
          <div className="space-y-4">
            {Object.entries(troubleshootingData.error_codes).map(([code, details], index) => (
              <div key={index} className="border-l-4 border-red-500 pl-4">
                <h4 className="font-mono font-bold text-gray-900">{code} - {details.meaning}</h4>
                <p className="text-gray-600 mt-1"><strong>Common Causes:</strong> {details.common_causes.join(', ')}</p>
                <p className="text-indigo-600 mt-1"><strong>Action:</strong> {details.action}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📞 Need More Help?</h3>
          <div className="space-y-2">
            <p className="text-gray-700">
              <strong>Email:</strong> <a href={`mailto:${troubleshootingData.contact_support.email}`} className="text-indigo-600 hover:text-indigo-800">{troubleshootingData.contact_support.email}</a>
            </p>
            <p className="text-gray-700">
              <strong>GitHub:</strong> <a href={troubleshootingData.contact_support.github} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">Report an Issue</a>
            </p>
            <p className="text-gray-700">
              <strong>Response Time:</strong> {troubleshootingData.contact_support.response_time}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeCategory) {
      case 'faq':
        return renderFAQ();
      case 'quickstart':
        return renderQuickstart();
      case 'features':
        return renderFeatures();
      case 'documentation':
        return renderDocumentation();
      case 'troubleshooting':
        return renderTroubleshooting();
      default:
        return renderFAQ();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Help & Support Center</h1>
          <p className="text-lg text-gray-600">Everything you need to master EF-Projex</p>
        </div>

        {/* Category Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${activeCategory === category.id
                      ? 'bg-indigo-600 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div>
          {renderContent()}
        </div>

        {/* Quick Access Banner */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">🎓 New to EF-Projex?</h3>
              <p className="text-indigo-100 mb-4">
                Start with our interactive customer onboarding guide to get up to speed quickly
              </p>
              <a
                href="/onboarding/customer"
                className="inline-flex items-center px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-semibold shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Start Customer Onboarding
              </a>
            </div>
            <div className="hidden md:block">
              <svg className="w-32 h-32 text-indigo-400 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
